import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, MapPin, DollarSign, Calculator, Shield, Plus,
  CheckCircle, AlertTriangle, CreditCard, Truck, FileText,
  ChevronDown, ChevronUp, ArrowRight, RefreshCw, X
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function GlobalCommercePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showAddMarket, setShowAddMarket] = useState(false);
  const [convertFrom, setConvertFrom] = useState('USD');
  const [convertTo, setConvertTo] = useState('EUR');
  const [convertAmount, setConvertAmount] = useState('100');
  const [convertResult, setConvertResult] = useState(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const profileData = await authService.getProfile();
      setUser(profileData.member);

      const [countriesRes, currenciesRes] = await Promise.allSettled([
        apiClient.get('/api/v1/global/countries'),
        apiClient.get('/api/v1/currencies'),
      ]);

      setCountries(countriesRes.status === 'fulfilled' ? countriesRes.value.data?.countries || [] : [
        { code: 'US', name: 'United States', flag: '🇺🇸', currency: 'USD', taxRate: '0-10%', active: true, paymentMethods: ['Credit Card', 'PayPal', 'Apple Pay'], logistics: ['USPS', 'FedEx', 'UPS'], complianceStatus: 'compliant', region: 'North America' },
        { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', currency: 'GBP', taxRate: '20% VAT', active: true, paymentMethods: ['Credit Card', 'PayPal', 'Klarna'], logistics: ['Royal Mail', 'DPD', 'DHL'], complianceStatus: 'compliant', region: 'Europe' },
        { code: 'DE', name: 'Germany', flag: '🇩🇪', currency: 'EUR', taxRate: '19% VAT', active: true, paymentMethods: ['Credit Card', 'PayPal', 'SOFORT', 'Klarna'], logistics: ['DHL', 'Hermes', 'DPD'], complianceStatus: 'compliant', region: 'Europe' },
        { code: 'JP', name: 'Japan', flag: '🇯🇵', currency: 'JPY', taxRate: '10%', active: true, paymentMethods: ['Credit Card', 'Konbini', 'PayPay'], logistics: ['Yamato', 'Sagawa', 'Japan Post'], complianceStatus: 'review', region: 'Asia Pacific' },
        { code: 'AU', name: 'Australia', flag: '🇦🇺', currency: 'AUD', taxRate: '10% GST', active: true, paymentMethods: ['Credit Card', 'PayPal', 'Afterpay'], logistics: ['Australia Post', 'StarTrack'], complianceStatus: 'compliant', region: 'Asia Pacific' },
        { code: 'CA', name: 'Canada', flag: '🇨🇦', currency: 'CAD', taxRate: '5-15% GST/HST', active: true, paymentMethods: ['Credit Card', 'PayPal', 'Interac'], logistics: ['Canada Post', 'Purolator', 'FedEx'], complianceStatus: 'compliant', region: 'North America' },
        { code: 'BR', name: 'Brazil', flag: '🇧🇷', currency: 'BRL', taxRate: '12-25%', active: false, paymentMethods: ['Credit Card', 'Pix', 'Boleto'], logistics: ['Correios', 'Jadlog'], complianceStatus: 'pending', region: 'Latin America' },
        { code: 'IN', name: 'India', flag: '🇮🇳', currency: 'INR', taxRate: '18% GST', active: false, paymentMethods: ['Credit Card', 'UPI', 'Paytm'], logistics: ['BlueDart', 'Delhivery', 'India Post'], complianceStatus: 'pending', region: 'Asia Pacific' },
      ]);

      setCurrencies(currenciesRes.status === 'fulfilled' ? currenciesRes.value.data?.currencies || [] : [
        { code: 'USD', name: 'US Dollar', symbol: '$' },
        { code: 'EUR', name: 'Euro', symbol: '€' },
        { code: 'GBP', name: 'British Pound', symbol: '£' },
        { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
        { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
        { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
        { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
        { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
      ]);
    } catch (error) {
      console.error('Failed to load global data:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    setConverting(true);
    try {
      const res = await apiClient.post('/api/v1/prices/convert', {
        from: convertFrom,
        to: convertTo,
        amount: parseFloat(convertAmount),
      });
      setConvertResult(res.data);
      toast.success('Conversion complete');
    } catch (error) {
      // Fallback with mock rate
      const mockRates = { USD: 1, EUR: 0.92, GBP: 0.79, JPY: 149.5, AUD: 1.53, CAD: 1.36, BRL: 4.97, INR: 83.1 };
      const fromRate = mockRates[convertFrom] || 1;
      const toRate = mockRates[convertTo] || 1;
      const result = (parseFloat(convertAmount) / fromRate) * toRate;
      setConvertResult({ converted: result.toFixed(2), rate: (toRate / fromRate).toFixed(4), from: convertFrom, to: convertTo });
    } finally {
      setConverting(false);
    }
  };

  const handleViewMarket = async (country) => {
    setSelectedCountry(country);
    try {
      const res = await apiClient.get(`/api/v1/global/${country.code}/market`);
      if (res.data) {
        setSelectedCountry({ ...country, ...res.data });
      }
    } catch (error) {
      // Use existing country data
    }
  };

  const handleAddMarket = () => {
    setShowAddMarket(true);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/login');
  };

  const getComplianceStyle = (status) => {
    const map = {
      compliant: { color: '#3EF1C7', label: 'Compliant', Icon: CheckCircle },
      review: { color: '#FFC857', label: 'Under Review', Icon: AlertTriangle },
      pending: { color: '#9CB0C7', label: 'Pending', Icon: FileText },
      'non-compliant': { color: '#F55454', label: 'Non-Compliant', Icon: AlertTriangle },
    };
    return map[status] || map.pending;
  };

  const activeCountries = countries.filter((c) => c.active);
  const inactiveCountries = countries.filter((c) => !c.active);
  const regions = [...new Set(countries.map((c) => c.region))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading global commerce...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Global Commerce</h1>
            <p className="text-sm text-[#9CB0C7]">Global Commerce</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              ← Dashboard
            </button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Globe className="w-8 h-8 text-[#7A5CFF]" />
            <div>
              <h2 className="text-3xl font-bold text-white">Global Commerce</h2>
              <p className="text-[#9CB0C7]">Multi-country setup, currencies, tax, and compliance</p>
            </div>
          </div>
          <button
            onClick={handleAddMarket}
            className="flex items-center gap-2 px-6 py-3 rounded-lg bg-[#7A5CFF] text-white font-semibold hover:bg-[#6B4FE0] transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Market
          </button>
        </div>

        {/* Region Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Active Markets', value: activeCountries.length, color: '#3EF1C7' },
            { label: 'Currencies', value: currencies.length, color: '#7A5CFF' },
            { label: 'Regions', value: regions.length, color: '#FFC857' },
            { label: 'Pending Setup', value: inactiveCountries.length, color: '#9CB0C7' },
          ].map((stat, idx) => (
            <div key={idx} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-4 text-center">
              <p className="text-2xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
              <p className="text-xs text-[#9CB0C7] mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Country Cards */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#3EF1C7]" />
            Active Markets
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeCountries.map((country) => {
              const compliance = getComplianceStyle(country.complianceStatus);
              const ComplianceIcon = compliance.Icon;
              return (
                <div
                  key={country.code}
                  onClick={() => handleViewMarket(country)}
                  className={`rounded-lg border bg-[#111A2E] p-5 cursor-pointer hover:scale-[1.01] transition-all ${
                    selectedCountry?.code === country.code ? 'border-[#7A5CFF]' : 'border-[#22304A]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{country.flag}</span>
                      <div>
                        <p className="text-sm font-semibold text-white">{country.name}</p>
                        <p className="text-xs text-[#9CB0C7]">{country.region}</p>
                      </div>
                    </div>
                    <span className="text-sm font-mono text-[#7A5CFF]">{country.currency}</span>
                  </div>

                  <div className="space-y-2 text-xs mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#9CB0C7] flex items-center gap-1"><DollarSign className="w-3 h-3" /> Tax Rate</span>
                      <span className="text-white font-semibold">{country.taxRate}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#9CB0C7] flex items-center gap-1"><CreditCard className="w-3 h-3" /> Payments</span>
                      <span className="text-white">{country.paymentMethods?.length} methods</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#9CB0C7] flex items-center gap-1"><Truck className="w-3 h-3" /> Logistics</span>
                      <span className="text-white">{country.logistics?.length} carriers</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 pt-2 border-t border-[#22304A]">
                    <ComplianceIcon className="w-3.5 h-3.5" style={{ color: compliance.color }} />
                    <span className="text-xs font-semibold" style={{ color: compliance.color }}>{compliance.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Inactive / Pending Markets */}
        {inactiveCountries.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#9CB0C7]" />
              Available Markets
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {inactiveCountries.map((country) => (
                <div key={country.code} className="rounded-lg border border-[#22304A] bg-[#111A2E]/50 p-4 opacity-70 hover:opacity-100 transition-opacity">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{country.flag}</span>
                    <div>
                      <p className="text-sm font-medium text-white">{country.name}</p>
                      <p className="text-xs text-[#9CB0C7]">{country.currency}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => toast.info('Market activation', { description: `${country.name} setup wizard coming soon` })}
                    className="w-full mt-2 px-3 py-1.5 rounded text-xs font-semibold border border-[#7A5CFF]/50 text-[#7A5CFF] hover:bg-[#7A5CFF]/10 transition-colors"
                  >
                    Activate Market
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Currency Converter + Country Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Currency Converter */}
          <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calculator className="w-5 h-5 text-[#FFC857]" />
              <h3 className="text-lg font-semibold text-white">Currency Converter</h3>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-5 gap-2 items-end">
                <div className="col-span-2">
                  <label className="text-xs text-[#9CB0C7] block mb-1">From</label>
                  <select
                    value={convertFrom}
                    onChange={(e) => setConvertFrom(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white text-sm focus:outline-none focus:border-[#FFC857]"
                  >
                    {currencies.map((c) => (
                      <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-center pb-2">
                  <ArrowRight className="w-5 h-5 text-[#9CB0C7]" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-[#9CB0C7] block mb-1">To</label>
                  <select
                    value={convertTo}
                    onChange={(e) => setConvertTo(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white text-sm focus:outline-none focus:border-[#FFC857]"
                  >
                    {currencies.map((c) => (
                      <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#9CB0C7] block mb-1">Amount</label>
                <input
                  type="number"
                  value={convertAmount}
                  onChange={(e) => setConvertAmount(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white text-sm focus:outline-none focus:border-[#FFC857]"
                />
              </div>
              <button
                onClick={handleConvert}
                disabled={converting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#FFC857] text-[#0B1120] font-semibold hover:bg-[#E5B44E] transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${converting ? 'animate-spin' : ''}`} />
                {converting ? 'Converting...' : 'Convert'}
              </button>
              {convertResult && (
                <div className="p-4 rounded-lg bg-[#0B1120] border border-[#FFC857]/30 text-center">
                  <p className="text-2xl font-bold text-white">{convertResult.converted} {convertResult.to}</p>
                  <p className="text-xs text-[#9CB0C7] mt-1">Rate: 1 {convertResult.from} = {convertResult.rate} {convertResult.to}</p>
                </div>
              )}
            </div>
          </div>

          {/* Country Detail / Tax Config */}
          <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-[#3EF1C7]" />
              <h3 className="text-lg font-semibold text-white">
                {selectedCountry ? `${selectedCountry.name} Details` : 'Select a Country'}
              </h3>
            </div>
            {selectedCountry ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">{selectedCountry.flag}</span>
                  <div>
                    <p className="text-lg font-semibold text-white">{selectedCountry.name}</p>
                    <p className="text-sm text-[#9CB0C7]">{selectedCountry.region} | {selectedCountry.currency}</p>
                  </div>
                </div>

                {/* Tax Configuration */}
                <div>
                  <p className="text-xs font-semibold text-[#9CB0C7] uppercase mb-2">Tax Configuration</p>
                  <div className="p-3 rounded bg-[#0B1120] border border-[#22304A]">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#9CB0C7]">Tax Rate</span>
                      <span className="text-[#FFC857] font-semibold">{selectedCountry.taxRate}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Methods */}
                <div>
                  <p className="text-xs font-semibold text-[#9CB0C7] uppercase mb-2">Payment Methods</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCountry.paymentMethods?.map((method, idx) => (
                      <span key={idx} className="px-2 py-1 rounded text-xs bg-[#7A5CFF]/10 text-[#7A5CFF] border border-[#7A5CFF]/30">
                        {method}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Logistics */}
                <div>
                  <p className="text-xs font-semibold text-[#9CB0C7] uppercase mb-2">Logistics Carriers</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCountry.logistics?.map((carrier, idx) => (
                      <span key={idx} className="px-2 py-1 rounded text-xs bg-[#3EF1C7]/10 text-[#3EF1C7] border border-[#3EF1C7]/30">
                        {carrier}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Regulatory Compliance Checklist */}
                <div>
                  <p className="text-xs font-semibold text-[#9CB0C7] uppercase mb-2">Regulatory Compliance</p>
                  <div className="space-y-2">
                    {[
                      { item: 'Tax registration', done: true },
                      { item: 'Privacy policy (local)', done: true },
                      { item: 'Consumer protection terms', done: selectedCountry.complianceStatus === 'compliant' },
                      { item: 'Import/export licenses', done: selectedCountry.complianceStatus === 'compliant' },
                      { item: 'Data residency compliance', done: false },
                    ].map((check, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        {check.done ? (
                          <CheckCircle className="w-4 h-4 text-[#3EF1C7]" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border border-[#22304A]" />
                        )}
                        <span className={check.done ? 'text-white' : 'text-[#9CB0C7]'}>{check.item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-[#9CB0C7]">
                <Globe className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">Click on a country card to view details</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add Market Modal */}
      {showAddMarket && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#111A2E] border border-[#22304A] rounded-xl p-8 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Add New Market</h3>
              <button onClick={() => setShowAddMarket(false)} className="text-[#9CB0C7] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <label className="block">
                <span className="text-sm text-[#9CB0C7]">Country</span>
                <select className="mt-1 w-full px-4 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white focus:outline-none focus:border-[#7A5CFF]">
                  <option>Select a country...</option>
                  <option>Mexico</option>
                  <option>France</option>
                  <option>South Korea</option>
                  <option>Singapore</option>
                  <option>UAE</option>
                </select>
              </label>
              <label className="block">
                <span className="text-sm text-[#9CB0C7]">Primary Currency</span>
                <select className="mt-1 w-full px-4 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white focus:outline-none focus:border-[#7A5CFF]">
                  {currencies.map((c) => (
                    <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-sm text-[#9CB0C7]">Tax Configuration</span>
                <input type="text" placeholder="e.g., 16% IVA" className="mt-1 w-full px-4 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white placeholder-[#9CB0C7] focus:outline-none focus:border-[#7A5CFF]" />
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddMarket(false)}
                className="px-4 py-2 rounded-lg border border-[#22304A] text-[#9CB0C7] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  toast.success('Market added', { description: 'New market configuration saved' });
                  setShowAddMarket(false);
                }}
                className="px-6 py-2 rounded-lg bg-[#7A5CFF] text-white font-semibold hover:bg-[#6B4FE0] transition-colors"
              >
                Add Market
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
